import { useCallback } from 'react'

const useCanvas = ({
    videoRef,
    canvasRef,
    cropData,
    aspect,
    width,
    height,
    minWidth,
    minHeight,
    videoLoaded,
    setVideoLoaded,
    isPlaying,
    type,
    autoStart,
    isSeekComplete = true,
    onAutoplayBlocked,
}) => {
    const getCanvasDimensions = useCallback(() => {
        let canvasWidth = Math.max(width, minWidth || 0)
        let canvasHeight = Math.max(height, minHeight || 0)

        if (aspect) {
            if (aspect > 1) {
                canvasHeight = width / aspect
            } else {
                canvasWidth = height * aspect
            }
        }

        return { width: canvasWidth, height: canvasHeight }
    }, [aspect, width, height])

    const drawFrame = useCallback(() => {
        const media = videoRef.current
        const canvas = canvasRef.current
        if (!media || !canvas || !videoLoaded) return

        const ctx = canvas.getContext('2d')
        const { width: canvasWidth, height: canvasHeight } = getCanvasDimensions()
        ctx.clearRect(0, 0, canvasWidth, canvasHeight)

        const mediaWidth = media.naturalWidth || media.videoWidth
        const mediaHeight = media.naturalHeight || media.videoHeight
        if (!mediaWidth || !mediaHeight) return

        if (cropData) {
            const { x, y, width: w, height: h } = cropData
            ctx.drawImage(media, x, y, w, h, 0, 0, canvasWidth, canvasHeight)
        } else {
            const mediaRatio = mediaWidth / mediaHeight
            const canvasRatio = canvasWidth / canvasHeight

            let drawW, drawH, drawX, drawY

            if (mediaRatio > canvasRatio) {
                drawW = canvasWidth
                drawH = canvasWidth / mediaRatio
                drawX = 0
                drawY = (canvasHeight - drawH) / 2
            } else {
                drawW = canvasHeight * mediaRatio
                drawH = canvasHeight
                drawX = (canvasWidth - drawW) / 2
                drawY = 0
            }

            ctx.drawImage(media, drawX, drawY, drawW, drawH)
        }

        if (isPlaying) {
            requestAnimationFrame(drawFrame)
        }
    }, [cropData, getCanvasDimensions, isPlaying, videoLoaded])

    const handleMediaLoad = useCallback(() => {
        const media = videoRef.current
        if (type === 'video' && media) {
            if (media.paused && autoStart) {
                media.play().catch(() => onAutoplayBlocked?.())
            } else if (!autoStart && media.currentTime !== 0) {
                // Only reset to 0 if no trim data - otherwise let useVideo handle the seek
                if (isSeekComplete) {
                    media.currentTime = 0
                }
            }
        }

        setVideoLoaded(true)

        const { width, height } = getCanvasDimensions()
        canvasRef.current.width = width
        canvasRef.current.height = height

        // Only draw immediately if seek is complete or not needed
        if (isSeekComplete) {
            setTimeout(() => drawFrame(), 100)
        }

        // For images specifically, force an additional immediate draw to ensure visibility
        if (type === 'image') {
            // Direct draw for images, bypassing videoLoaded state timing issues
            // Use a slightly longer delay to ensure crop data is available
            setTimeout(() => {
                const media = videoRef.current
                const canvas = canvasRef.current
                if (!media || !canvas) return

                const ctx = canvas.getContext('2d')
                const { width: canvasWidth, height: canvasHeight } = getCanvasDimensions()
                ctx.clearRect(0, 0, canvasWidth, canvasHeight)

                const mediaWidth = media.naturalWidth || media.width
                const mediaHeight = media.naturalHeight || media.height
                if (!mediaWidth || !mediaHeight) return

                if (cropData) {
                    const { x, y, width: w, height: h } = cropData
                    ctx.drawImage(media, x, y, w, h, 0, 0, canvasWidth, canvasHeight)
                } else {
                    const mediaRatio = mediaWidth / mediaHeight
                    const canvasRatio = canvasWidth / canvasHeight

                    let drawW, drawH, drawX, drawY

                    if (mediaRatio > canvasRatio) {
                        drawW = canvasWidth
                        drawH = canvasWidth / mediaRatio
                        drawX = 0
                        drawY = (canvasHeight - drawH) / 2
                    } else {
                        drawW = canvasHeight * mediaRatio
                        drawH = canvasHeight
                        drawX = (canvasWidth - drawW) / 2
                        drawY = 0
                    }

                    ctx.drawImage(media, drawX, drawY, drawW, drawH)
                }
            }, 50)

            // Also trigger drawFrame after state updates
            setTimeout(() => {
                drawFrame()
            }, 150)
        }
    }, [autoStart, cropData, drawFrame, getCanvasDimensions, setVideoLoaded, type, isSeekComplete, onAutoplayBlocked])

    return {
        drawFrame,
        handleMediaLoad,
        getCanvasDimensions,
    }
}

export default useCanvas
